<template>
  <div class="theme-bar-chart">
    <Bar
      :chart-data="chartData"
      :chart-options="chartOptions"
    />
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'ThemeBarChart',
  components: { Bar },
  
  props: {
    data: {
      type: Array,
      required: true
    },
    height: {
      type: [Number, String],
      default: 400
    },
    legendFontColor: {
      type: String,
      default: null
    },
    labelFontColor: {
      type: String,
      default: null
    },
    bgColor: {
      type: String,
      default: 'transparent'
    }
  },

  data() {
    return {
      isDarkMode: document.documentElement.classList.contains('dark'),
      themeObserver: null
    };
  },

  mounted() {
    this.themeObserver = new MutationObserver(() => {
      this.isDarkMode = document.documentElement.classList.contains('dark');
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  },

  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  computed: {
    chartData() {
      if (!this.data || !Array.isArray(this.data)) {
        return {
          labels: [],
          datasets: []
        };
      }

      const colors = ['#3b82f6', '#22c55e'];
      
      return {
        labels: this.data.map(item => item.label),
        datasets: [{
          label: 'Value',
          data: this.data.map(item => item.value),
          backgroundColor: colors,
          borderWidth: 0,
          barThickness: 60
        }]
      };
    },

    chartOptions() {
      const textColor = this.labelFontColor || (this.isDarkMode ? '#94a3b8' : '#374151');
      const gridColor = this.isDarkMode ? '#1e293b' : '#e5e7eb';
      const tooltipBg = this.isDarkMode ? '#1e293b' : '#ffffff';
      const tooltipBorder = this.isDarkMode ? '#334155' : '#e5e7eb';

      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              color: this.legendFontColor || textColor,
              padding: 15,
              usePointStyle: true,
              pointStyle: 'circle'
            }
          },
          tooltip: {
            backgroundColor: tooltipBg,
            titleColor: textColor,
            bodyColor: textColor,
            borderColor: tooltipBorder,
            borderWidth: 1,
            padding: 12,
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed.y;
                return `${label}: ${value.toLocaleString()}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              color: textColor
            }
          },
          y: {
            grid: {
              color: gridColor,
              drawBorder: false
            },
            ticks: {
              color: textColor,
              callback: (value) => value.toLocaleString()
            }
          }
        }
      };
    }
  }
};
</script>
